import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react'

import { type SoapContentItem } from '@/shared/types/yakurekiSoap'

// 編集の対象は常に 1 つだけ。「行の編集」と「未保存の空行」が同時に立つ状態を型で作れなく
// する (滝沢店の複製・入力消失はどちらもこの同時成立から出ていた。log/207 work-log/41 §2.2)。
// idle 以外の値そのものが「編集セッション」の同一性を持つ (= 非同期完了時の照合に使う。
// 世代番号 sessionRef の代わり)。draft を target の中に置かないのはそのため — 1 打鍵ごとに
// 同一性が変わると照合できなくなる。「持ち主のない下書き」に戻さないための担保は、
// 入力欄を出す行を target が決める (= 同時に 2 つ開かない) ことで型側に残している。
// newLine の anchor は「開いた時点で afterIndex に居た行の本文」。挿入位置の同一性も index
// しかないため、line の original と同じ理由で保存直前の突合に使う (section 先頭 = afterIndex
// -1 は突合相手が居ないので null)。
type EditTarget<K extends string> =
  | { mode: 'idle' }
  | { mode: 'line'; key: K; index: number; original: string }
  | { mode: 'newLine'; key: K; afterIndex: number; anchor: string | null }

// useLineEditing の返り値の契約。SectionRenderer が SOAP/報告書 共通で受けるための型。
export interface LineEditing<K extends string> {
  editingText: string
  setEditingText: Dispatch<SetStateAction<string>>
  /** この行を inline 編集中か (= 入力欄に差し替えて描く行かどうか)。 */
  isEditing: (key: K, index: number) => boolean
  /** この行の直下に未保存の空行が開いているか (afterIndex = -1 は section 先頭)。 */
  hasNewLineAfter: (key: K, afterIndex: number) => boolean
  startEdit: (key: K, index: number, content: string) => void
  /** Escape / 明示的な離脱。保存はしない。 */
  cancel: () => void
  openNewLine: (key: K, afterIndex: number) => void
  saveEdit: (
    key: K,
    index: number,
    opts?: { continueAfterSave?: boolean }
  ) => Promise<void>
  saveNewLine: (
    key: K,
    afterIndex: number,
    opts?: { continueAfterSave?: boolean }
  ) => Promise<void>
}

// SOAP 行 / 訪問報告書 行の inline 編集・新規行追加に共通する「状態機械」を 1 箇所に集約する hook。
// 行クリックで編集に入り、Enter 保存の直後に続けて次の空行を開き連続入力できる (= Notion 標準の
// Enter 挙動。FB MTG 2026-06-30 要望。log/171 work-log/01 §3.3)。何も打たず再度 Enter (または
// blur/Escape) で空行は保存されず編集終了する。blur は「保存して編集を抜ける」。空文字保存は
// 行削除、空行は永続化しない。
//
// 保存系 (saveEdit / saveNewLine) は「自分が今の編集対象か」を入口で確かめ、違えば何もしない。
// Enter で次の行へ移った直後に届く旧入力欄の blur はこれで無効になるので、呼び出し側に
// 二重発火を抑止する握手 (旧 continuingRef) は要らない。
//
// 差分 (SOAP / 訪問報告書) は itemsOf (key→現行 items の解決) と commit (key の items 差し替えの
// 永続化。成功=true。成功トーストは commit 側で任意に出す) の 2 つの注入だけで吸収する。
// key 型 K はセクションキー (SOAP=SoapSectionKeys / 訪問報告書=string) を汎用化する。

interface UseLineEditingArgs<K extends string> {
  itemsOf: (key: K) => SoapContentItem[]
  commit: (key: K, nextItems: SoapContentItem[]) => Promise<boolean>
}

export const useLineEditing = <K extends string>({
  itemsOf,
  commit,
}: UseLineEditingArgs<K>): LineEditing<K> => {
  // 描画用 (render 中に読む)。イベントの前後関係を判定する側は targetRef を読む。
  const [target, setTarget] = useState<EditTarget<K>>({ mode: 'idle' })
  const [editingText, setEditingText] = useState('')
  const targetRef = useRef<EditTarget<K>>(target)

  // 対象と下書きは必ず同時に移す (下書きだけが別の行に残る状態を作らない)。
  const moveTo = useCallback((next: EditTarget<K>, draft: string) => {
    targetRef.current = next
    setTarget(next)
    setEditingText(draft)
  }, [])

  const isEditing = (key: K, index: number) =>
    target.mode === 'line' && target.key === key && target.index === index

  const hasNewLineAfter = (key: K, afterIndex: number) =>
    target.mode === 'newLine' &&
    target.key === key &&
    target.afterIndex === afterIndex

  // 別の行 (別 section を含む) の編集に入る。開いていた空行はここで畳まれる。空行に打った
  // 文字は先に届く blur が保存するので、ここでの破棄は blur が来ない経路の保険。
  const startEdit = useCallback(
    (key: K, index: number, content: string) => {
      moveTo({ mode: 'line', key, index, original: content }, content)
    },
    [moveTo]
  )

  const cancel = useCallback(() => {
    moveTo({ mode: 'idle' }, '')
  }, [moveTo])

  const openNewLine = useCallback(
    (key: K, afterIndex: number) => {
      const anchor = itemsOf(key)[afterIndex]?.content ?? null
      moveTo({ mode: 'newLine', key, afterIndex, anchor }, '')
    },
    [itemsOf, moveTo]
  )

  // 1 行の編集を保存。該当 key の items を差し替えて commit に渡す。空文字は行削除。
  // continueAfterSave (Enter 確定時のみ) が立ち、行を削除しなかった場合、保存直後にこの行の
  // 直後へ空行を開き連続入力できるようにする (削除時は継続しない)。
  const saveEdit = useCallback(
    async (key: K, index: number, opts?: { continueAfterSave?: boolean }) => {
      const session = targetRef.current
      // 既に別の対象へ移っている (= Enter の直後に届いた旧入力欄の blur 等)。
      if (
        session.mode !== 'line' ||
        session.key !== key ||
        session.index !== index
      ) {
        return
      }
      const items = itemsOf(key)
      const current = items[index]
      // 行の同一性が index しかないので、開いている裏で items が入れ替わると別の行を無言で
      // 上書きしてしまう。編集を開いた時点の本文と突合し、食い違えば保存せず離脱する
      // (根治は行 id の永続化 = 二段化 PR。ここでは誤爆だけを止める)。
      if (current == null || current.content !== session.original) {
        moveTo({ mode: 'idle' }, '')
        return
      }
      const next = editingText.trim()
      // 継続で開く空行の anchor は「この行が保存後に持つ本文」= 直下に挿す相手そのもの。
      const continueTo = (anchor: string): EditTarget<K> =>
        opts?.continueAfterSave
          ? { mode: 'newLine', key, afterIndex: index, anchor }
          : { mode: 'idle' }
      if (next === current.content.trim()) {
        moveTo(continueTo(current.content), '')
        return
      }
      const nextItems = [...items]
      const deleted = next.length === 0
      if (deleted) {
        nextItems.splice(index, 1)
      } else {
        nextItems[index] = { ...current, content: next }
      }
      const ok = await commit(key, nextItems)
      // 往復中にユーザーが別のセッションへ移っていたら画面状態には触らない (保存は成立済み)。
      if (targetRef.current !== session) return
      moveTo(ok && !deleted ? continueTo(next) : { mode: 'idle' }, '')
    },
    [editingText, itemsOf, commit, moveTo]
  )

  // 未保存の空行 (「行を追加」または Enter 連続追加で開いたもの) を確定。非空なら afterIndex の
  // 直後に挿入して commit。continueAfterSave が立てば保存直後に次の空行を開く。空文字なら
  // 何も挿入せず離脱する (= 空行は永続化しない)。
  const saveNewLine = useCallback(
    async (
      key: K,
      afterIndex: number,
      opts?: { continueAfterSave?: boolean }
    ) => {
      const session = targetRef.current
      if (
        session.mode !== 'newLine' ||
        session.key !== key ||
        session.afterIndex !== afterIndex
      ) {
        return
      }
      const text = editingText.trim()
      if (text.length === 0) {
        moveTo({ mode: 'idle' }, '')
        return
      }
      const items = itemsOf(key)
      // 挿入位置も index しか同一性が無いので、開いている裏で items が入れ替わると
      // 別の行の直下に挿し込んでしまう (= 打った内容が他の行に帰属する)。開いた時点の
      // 直上行と突合し、食い違えば挿さずに離脱する (saveEdit の本文突合と対。根治は
      // 行 id の永続化 = 二段化 PR)。
      if (
        session.anchor !== null &&
        items[afterIndex]?.content !== session.anchor
      ) {
        moveTo({ mode: 'idle' }, '')
        return
      }
      const insertPos = afterIndex + 1
      const nextItems = [...items]
      nextItems.splice(insertPos, 0, {
        content: text,
        is_by_ai: false,
        is_selected: true,
      })
      const ok = await commit(key, nextItems)
      if (targetRef.current !== session) return
      moveTo(
        ok && opts?.continueAfterSave
          ? { mode: 'newLine', key, afterIndex: insertPos, anchor: text }
          : { mode: 'idle' },
        ''
      )
    },
    [editingText, itemsOf, commit, moveTo]
  )

  return {
    editingText,
    setEditingText,
    isEditing,
    hasNewLineAfter,
    startEdit,
    cancel,
    openNewLine,
    saveEdit,
    saveNewLine,
  }
}
